import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  pending: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    searchTerm: '',
    sortOption: 'feedbackPriority',
    isLoading: false,
    error: null
  },
  reviewed: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    searchTerm: '',
    sortOption: 'feedbackPriority',
    isLoading: false,
    error: null
  },
  gameReports: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    searchTerm: '',
    sortOption: 'feedbackPriority',
    isLoading: false,
    error: null
  },
  gameVideos: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    searchTerm: '',
    sortOption: 'default',
    isLoading: false,
    error: null
  }
};

const paginationSlice = createSlice({
  name: 'pagination',
  initialState,
  reducers: {
    // Set current page
    setCurrentPage: (state, action) => {
      const { page, component = 'pending' } = action.payload;
      state[component].currentPage = page;
    },
    
    // Set total pages
    setTotalPages: (state, action) => {
      const { totalPages, component = 'pending' } = action.payload;
      state[component].totalPages = totalPages;
    },
    
    // Set total records
    setTotalRecords: (state, action) => {
      const { totalRecords, component = 'pending' } = action.payload;
      state[component].totalRecords = totalRecords;
    },
    
    // Set search term
    setSearchTerm: (state, action) => {
      const { searchTerm, component = 'pending' } = action.payload;
      state[component].searchTerm = searchTerm;
      // Reset to first page when search term changes
      state[component].currentPage = 1;
    },
    
    // Set sort option
    setSortOption: (state, action) => {
      const { sortOption, component = 'pending' } = action.payload;
      state[component].sortOption = sortOption;
      // Reset to first page when sort option changes
      state[component].currentPage = 1;
    },
    
    // Set loading state
    setLoading: (state, action) => {
      const { isLoading, component = 'pending' } = action.payload;
      state[component].isLoading = isLoading;
    },
    
    // Set error
    setError: (state, action) => {
      const { error, component = 'pending' } = action.payload;
      state[component].error = error;
    },
    
    // Reset pagination state
    resetPagination: (state, action) => {
      const { component = 'pending' } = action.payload;
      const defaultSortOption = component === 'gameVideos' ? 'default' : 'feedbackPriority';
      
      state[component] = {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        searchTerm: '',
        sortOption: defaultSortOption,
        isLoading: false,
        error: null
      };
    },
    
    // Update pagination state from API response
    updatePaginationFromResponse: (state, action) => {
      const { 
        totalRecords, 
        currentPage = 1, 
        searchTerm = '', 
        sortOption,
        component = 'pending' 
      } = action.payload;
      
      const totalPages = Math.ceil(totalRecords / 12); // VIDEO_SEARCH_API_LIMIT = 12
      const defaultSortOption = component === 'gameVideos' ? 'default' : 'feedbackPriority';
      
      state[component] = {
        currentPage,
        totalPages: Math.max(1, totalPages),
        totalRecords,
        searchTerm,
        sortOption: sortOption || defaultSortOption,
        isLoading: false,
        error: null
      };
    }
  }
});

// Export actions
export const {
  setCurrentPage,
  setTotalPages,
  setTotalRecords,
  setSearchTerm,
  setSortOption,
  setLoading,
  setError,
  resetPagination,
  updatePaginationFromResponse
} = paginationSlice.actions;

// Export selectors
export const selectPagination = (state, component = 'pending') => state.pagination[component];
export const selectCurrentPage = (state, component = 'pending') => state.pagination[component].currentPage;
export const selectTotalPages = (state, component = 'pending') => state.pagination[component].totalPages;
export const selectTotalRecords = (state, component = 'pending') => state.pagination[component].totalRecords;
export const selectSearchTerm = (state, component = 'pending') => state.pagination[component].searchTerm;
export const selectSortOption = (state, component = 'pending') => state.pagination[component].sortOption;
export const selectIsLoading = (state, component = 'pending') => state.pagination[component].isLoading;
export const selectError = (state, component = 'pending') => state.pagination[component].error;

// Export reducer
export default paginationSlice.reducer; 